import React, { useEffect, useState } from 'react';
//components
import Select from "react-select"
import Label from '../../../ui/labels/Label';
import { PoiDialogButton } from './poiDialogComponents/PoiDialogButton';
import { PoiDialogInput } from './poiDialogComponents/PoiDialogInput';
//hooks
import usePoiGroupService from '../../../../hooks/pois/usePoiGroupsService'
import useConfirmDialog from '../../../../hooks/useConfirmDialog'
import { PoiGroupDialogDetailsTooltip } from '@components/ui/tooltips/PoiTooltips';
/**
 * Dialog box for updating groups for a single poi, or adding a new group
 * 
 * @param {string} poiName 
 * @param {string} poiDescription 
 * @param {number} poiId 
 * @param {Object[]} poiGroupData - poi groups in format of {POIGroupID: int, POIGroup_Name: string}
 * @param {number} selectedPoiJob - currently selected JobID
 * @param {Object} poiDialogHandlers - handler functions from usePoiDialogHandlers
 * 
 * @returns {JSX.Element}
 * 
 * @category Components - Map
 * 
 */

const PoiGroupDialog = ({poiName, poiDescription, poiId, poiGroupData, selectedPoiJob, poiDialogHandlers}) => {

  const {formatPoiGroupOptions, fetchPOIGroupsByUser, poiGroupOptions, usersGroupData, setPoiGroupOptions} = usePoiGroupService()
  

  /** STATE **/

  const [selectedPoiGroup, setSelectedPoiGroup] = useState() //group that user is interested in - will be able to view and/or edit(depending on isEditor permissions) the alt poi name and description for this group, data fills dialog inputs
  const [currentPoiGroups, setCurrentPoiGroups] = useState(formatPoiGroupOptions(poiGroupData, true)) //all the (user's) groups this poi is in
  const [altPoiName, setAltPoiName] = useState("") //for poi in selected group
  const [altPoiDescription, setAltPoiDescription] = useState("") //for poi in selected group
  
  const [newPoiGroupName, setNewPoiGroupName] = useState(`${poiName} group`)
  const [newPoiGroupDescription, setNewPoiGroupDescription] = useState('')
  
  const [componentToShow, setComponentToShow] = useState({
      currentGroups: true,
      addToGroup: false,
      createNewGroup: false
  })

  const [isGroupEditor, setIsGroupEditor] = useState(false)

  const [ConfirmDialog, requestConfirm] = useConfirmDialog()

  //get poi group options when component loads, excluding option for "all groups"
  //fills dropdown in add to group area
  useEffect(() => {
    fetchPOIGroupsByUser(selectedPoiJob, true) 
  }, [])

  /** HANDLER FUNCTIONS **/

  //handle new group save
  const handleNewPoiGroupSave = async() => {
    //call api
    const response = await poiDialogHandlers.handleAddNewPoiGroup({
      POIGroup_Name: newPoiGroupName,
      Description: newPoiGroupDescription,
      JobID: selectedPoiJob,
      pois: [{
        POIID: poiId,
        POI_Name: altPoiName,
        Description: altPoiDescription
      }]
    })

    //if successful, add new group to groupOptions
    if(response.success){
      const newGroupID = response.data.POIGroupID
      const newGroupOption = {label: newPoiGroupName, value: newGroupID, IsEditor: true}
      
      //add the new group to the dropdown options
      setPoiGroupOptions(prevOptions => {
        return [...prevOptions, newGroupOption]
      })
      setCurrentPoiGroups(prevOptions => {
        return [...prevOptions, newGroupOption]
      })

      fetchPOIGroupsByUser(selectedPoiJob, true)
      //change component back to currentGroups
      handleChangeComponent("currentGroups")

    }

  }

  //save alt name and description
  const handleSavePoiDetails = async() => {
    const poiGroup = usersGroupData.find(poiGroup => poiGroup.POIGroupID === selectedPoiGroup.value)

    const linkedPOI = poiGroup.LinkedPOIs?.find(linkedPOI => linkedPOI.POIID === poiId)

    await poiDialogHandlers.handleUpdateLinkedPOI(
      selectedPoiGroup.value, 
      {
        ...linkedPOI,
        POI_Name: altPoiName,
        Description: altPoiDescription
      },
      poiId
    )

    fetchPOIGroupsByUser(selectedPoiJob, true)
  }


  const handleAddToGroup = async() => {
    const response = await poiDialogHandlers.handleAddPoiToGroup(
      selectedPoiGroup, 
      {
        POI_Name: altPoiName,
        Description: altPoiDescription,
        POIID: poiId
      }, 
      selectedPoiJob
    )

    if(response.success){
      fetchPOIGroupsByUser(selectedPoiJob, true)
      setCurrentPoiGroups(prevGroups => [...prevGroups, selectedPoiGroup])
  
      handleChangeComponent("currentGroups")
    }

  }

  const handleRemovePoiFromGroup = async() => {
    const isConfirmed = await requestConfirm("Are you sure you want to remove the POI from this group?", "Yes, Remove POI", "Cancel")

    if(!isConfirmed) return

    const response = await poiDialogHandlers.handleRemovePoiFromGroup(
      selectedPoiGroup, 
      {
        POIID: poiId
      }, 
      selectedPoiJob
    )

    if(response.success){
      fetchPOIGroupsByUser(selectedPoiJob, true)

      setCurrentPoiGroups(prevGroups => {
        const updatedGroups = prevGroups.filter(group => group.value !== selectedPoiGroup.value)
  
        return updatedGroups
      })
      
      handleChangeComponent("currentGroups")
    }
  }

  const handleChangeComponent = (changeTo) => {
    setComponentToShow({
        currentGroups: false,
        addToGroup: false,
        createNewGroup: false,
        [changeTo]: true
    })

    setSelectedPoiGroup(null)

    //if adding to group or creating new group, fill in name and description with either original poi data, or linkedpoi data (if poi fetched by group)
    if(changeTo === "addToGroup" || changeTo === "createNewGroup"){
      setAltPoiName(poiName)
      setAltPoiDescription(poiDescription)
    } else {
      //otherwise, don't fill it in, user should select group from dropdown to see details
      setAltPoiName("")
      setAltPoiDescription("")
    }

  }

  const handleSelectionChange = (selectedGroup) => {
    //update selectedGroup state
    setSelectedPoiGroup(selectedGroup)
    
    if(componentToShow.addToGroup){
      setIsGroupEditor(true) //can always edit, since groups in this dropdown are already ones you can edit
      return
    }

    if(componentToShow.currentGroups){
      setIsGroupEditor(selectedGroup.IsEditor)
    }

    //update poi name and description to alt description
    const poiGroup = usersGroupData.find(poiGroup => poiGroup.POIGroupID === selectedGroup.value)

    const linkedPOI = poiGroup.LinkedPOIs?.find(linkedPOI => linkedPOI.POIID === poiId)

    setAltPoiName(linkedPOI?.POI_Name ? linkedPOI.POI_Name : "")
    setAltPoiDescription(linkedPOI?.Description ? linkedPOI.Description : "")

  }

  const handleSave = () => {

    if(componentToShow.createNewGroup){
      handleNewPoiGroupSave()
    }
    else if(componentToShow.addToGroup){
      handleAddToGroup()
    } else {
      handleSavePoiDetails()
    }

  }

  /** MAIN COMPONENT **/
  return (
    <>
      <div
        className='border border-black rounded p-4 flex flex-col justify-center min-h-[250px]'
      >
          {/* CURRENT GROUPS COMPONENT - the user's groups that POI is in */}

          {componentToShow.currentGroups && (
            <div className={`flex flex-col ${currentPoiGroups.length > 0 ? 'mb-4' : ''}`}>

              <Label>{currentPoiGroups.length > 0 ? 'This POI is in your groups:' : 'This POI is not in any of your groups'}</Label>

              {currentPoiGroups.length > 0 && (
                <Select
                  value={selectedPoiGroup}
                  options={currentPoiGroups}
                  name="poiGroupDropdown"
                  onChange={(x) => handleSelectionChange(x)}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999, width: 300}) }}
                  placeholder="Select group to view details..."
               />
              )}

              {/* if user is not editor of the current group, tell them they cannot make changes. they can still view the alt name and description though */}
              {selectedPoiGroup && !isGroupEditor && <p className='text-xs mt-4 mb-2 max-w-72'> 
                  <i className="fa-solid fa-circle-exclamation mr-2 text-yellow-500" ></i>
                  You are not an editor of this POI group and cannot make changes.
                </p>
              }

              {/* if a group is selected, and user is an editor, allow them to remove POI */}
              {selectedPoiGroup && isGroupEditor && (            
              
                <PoiDialogButton
                  text="Remove POI From This Group"
                  variant="danger"
                  onClick={() => handleRemovePoiFromGroup()}
                  className={`mt-4 max-w-max ${!isGroupEditor ? 'hover:cursor-not-allowed' : null}`}
                />
              )}

            </div>
          )}

        {/* ADD TO GROUP COMPONENT */}

        {componentToShow.addToGroup && (
            <div className='mb-6 flex flex-col'>

              <Label>Select group to add POI to</Label>

              <p className='text-xs my-2 max-w-72'> 
                This list shows groups you can edit that don’t already include this POI.
              </p>

              {/* all the user's groups, in which they are an editor, that POI is not currently in */}
              <Select
                value={selectedPoiGroup}
                options={poiGroupOptions.filter(option => option.IsEditor && currentPoiGroups.every(currentGroup => currentGroup.value !== option.value))}
                name="poiGroupDropdown"
                onChange={(x) => handleSelectionChange(x)}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999, width: 300}) }}
              />

              <PoiDialogButton
                text="Cancel"
                variant="cancel"
                onClick={() => handleChangeComponent("currentGroups")}
                className="mt-2 max-w-max self-end"
              />

            </div>
          )}


          {/* CREATE NEW GROUP COMPONENT */}

          {componentToShow.createNewGroup && (
            <div className='mb-6 flex flex-col'>

                <Label>Create new group</Label>

                {/* new group name */}
                
                <PoiDialogInput
                  label="Group name"
                  value={newPoiGroupName}
                  onChange={(e) => setNewPoiGroupName(e.target.value)}
                  boldLabel={false}
                />

                {/* new group description */}
                
                <PoiDialogInput
                  label="Description"
                  value={newPoiGroupDescription}
                  onChange={(e) => setNewPoiGroupDescription(e.target.value)}
                  placeholder="New group description..."
                  boldLabel={false}
                />

                <PoiDialogButton
                  text="Cancel"
                  variant="cancel"
                  onClick={() => handleChangeComponent("currentGroups")}
                  className="mt-2 max-w-max self-end"
                />


            </div>

          )}

          {/* POI Name and description section - 'alt name' and 'alt description' behind the scenes, fill with data depending on what group is selected */}
          {!(componentToShow.currentGroups && currentPoiGroups.length === 0) && (
            <>
              <div>

                <Label>
                  Your POI Details
                  <PoiGroupDialogDetailsTooltip />
                </Label>

                {/* new group name */}

                <PoiDialogInput
                  label="POI Name"
                  value={altPoiName}
                  onChange={(e) => setAltPoiName(e.target.value)}
                  placeholder='POI Name'
                  disabled={!isGroupEditor && componentToShow.currentGroups} //always enabled if adding to (editable) groups or creating new group
                  boldLabel={false}
                />

                {/* new group description */}

                <PoiDialogInput
                  label="Description"
                  value={altPoiDescription}
                  onChange={(e) => setAltPoiDescription(e.target.value)}
                  placeholder='POI Description'
                  disabled={!isGroupEditor && componentToShow.currentGroups} //always enabled if adding to (editable) groups or creating new group
                  boldLabel={false}
                />

              </div>

              <PoiDialogButton
                text={componentToShow.currentGroups ? "Save POI" : "Save Group and POI"}
                variant="primary"
                onClick={() => handleSave()}
                className={`mt-4 mb-2 self-end ${componentToShow.currentGroups && (!isGroupEditor || !selectedPoiGroup) ? 'hover:cursor-not-allowed' : null}`}
                disabled={componentToShow.currentGroups && (!isGroupEditor || !selectedPoiGroup)}
              />
              
            </>
          )}

          {/* OTHER ACTIONS - show other actions user can take like adding to group or creating new group */}
          {/* keeping below poi details to not disrupt the flow of currentGroups component */}

          {componentToShow.currentGroups && (
            <div className='my-4'>
              
              {currentPoiGroups.length > 0 && <Label>Other group actions</Label>}
                      
              <div className='flex'>
                {/* if there are still groups in which the user is an editor, that the POI has not already been added to, show 'Add' button */}
                {
                  (poiGroupOptions.filter(option => option.IsEditor)).length > 0 && (
                              
                    <PoiDialogButton
                      text="Add POI To My Groups"
                      variant="primary"
                      onClick={() => handleChangeComponent("addToGroup")}
                      className="mt-2 mr-2 max-w-max"
                    />
                  )
                }
            
                <PoiDialogButton
                  text="Create New Group"
                  variant="primary"
                  onClick={() => handleChangeComponent("createNewGroup")}
                  className="mt-2 max-w-max"
                />
              </div>
            </div>

          )}
          
        <hr className='my-4'></hr>

        <PoiDialogButton
          text="Back To POI"
          variant="cancel"
          onClick={() => poiDialogHandlers.handleChangeActiveComponent("PoiDialog")}
          className="self-end"
        />

      </div>
      
      {/* if user tries to remove POI from group, ask if they are sure */}
      <ConfirmDialog />

    </>
  )
};

export default PoiGroupDialog;
